import React, { useState, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { SettingsModal } from "./components/SettingsModal";
import BrandProfile from "./components/BrandProfile";
import HistoryView from "./components/HistoryView";
import DashboardView from "./components/DashboardView";
import AppLayout from "./components/layout/AppLayout";
import { BrandModal } from "./components/BrandModal";
import { scrapeCaption, isValidInstagramUrl } from "./utils/instagram";
import type { CommentResult, View, Brand, AIConfig } from "./types";
import { getBackend } from "./lib/backend";

const App: React.FC = () => {
  const [view, setView] = useState<View>("dashboard");
  const [links, setLinks] = useState<string[]>([]);
  const [results, setResults] = useState<Record<string, CommentResult>>({});
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  // --- AI Config State ---
  const [aiConfig, setAiConfig] = useState<AIConfig>(() => {
    const saved = localStorage.getItem("ai_config");
    if (saved) {
      try { return JSON.parse(saved); } catch (e) { console.error("Failed to parse config", e); }
    }
    // Fallback migration or default
    return {
      provider: "gateway",
      model: "",
      keys: {
        gateway: localStorage.getItem("openai_api_key") || "", // Migrate old key
        formatted: undefined // clean up
      }
    } as AIConfig;
  });

  const [activeBrand, setActiveBrand] = useState<Brand | null>(null);
  const [generationCount, setGenerationCount] = useState(1);
  const [isABTest, setIsABTest] = useState(false);
  const [isBrandModalOpen, setIsBrandModalOpen] = useState(false);

  const generateCommentRef = useRef<Function | null>(null);

  // ... fetchActiveBrand ...

  const fetchActiveBrand = async () => {
    try {
      const backend = await getBackend();
      const data = await backend.getActiveBrand();
      setActiveBrand(data);
    } catch (e) {
      console.error("Failed to fetch active brand", e);
    }
  };

  useEffect(() => {
    fetchActiveBrand();
  }, [view]);

  const updateResult = (url: string, update: Partial<CommentResult>) => {
    setResults(prev => ({
      ...prev,
      [url]: { ...(prev[url] || { status: "scraping", url, caption: "", comment: "" }), ...update }
    }));
  };

  const generateComment = async (url: string, caption: string) => {
    if (!activeBrand) {
      updateResult(url, { status: "error", error: "Please set an active Brand Profile first." });
      return;
    }

    // Validate config before sending
    if (aiConfig.provider === 'gateway' && !aiConfig.keys.gateway) {
      updateResult(url, { status: "error", error: "Missing Gateway API Key in settings." });
      setIsSettingsOpen(true);
      return;
    }
    // Add validations for other providers...

    updateResult(url, { status: "generating", caption });

    try {
      const backend = await getBackend();
      const comments = await backend.generateComments(
        url,
        caption,
        activeBrand.id!,
        {
          count: generationCount,
          abTest: isABTest,
          config: aiConfig
        }
      );

      updateResult(url, { status: "done", comment: comments[0], comments: comments });
    } catch (err: any) {
      console.error("Agent generation failed:", err);
      updateResult(url, { status: "error", error: err.message });
    }
  };

  useEffect(() => {
    generateCommentRef.current = generateComment;
  }, [activeBrand, aiConfig, generationCount, isABTest]);

  const handleScraping = async (url: string, forceRegenerate = false) => {
    const existingResult = results[url];
    let caption = existingResult?.caption || "";

    // If forcing regeneration, skip to generation with existing caption
    if (forceRegenerate && caption && caption !== "No caption found (video or image post)") {
      updateResult(url, { status: "generating", caption });
      await generateComment(url, caption);
      return;
    }

    if (!caption) {
      updateResult(url, { status: "scraping" });
      try {
        caption = await scrapeCaption(url);
      } catch (err: any) {
        updateResult(url, { status: "error", error: err.message });
        return;
      }
    }

    if (!caption || caption === "No caption found (video or image post)") {
      updateResult(url, { status: "error", error: "Failed to extract caption. Please ensure the post is public." });
      return;
    }

    await generateComment(url, caption);
  };

  const handleStartProcessing = () => {
    if (!activeBrand) {
      setIsBrandModalOpen(true);
      return;
    }
    links.forEach(url => {
      const result = results[url];
      // Skip empty or invalid Instagram URLs
      if (!isValidInstagramUrl(url)) return;
      // Skip if currently loading
      if (result?.status === "scraping" || result?.status === "generating") return;
      // Regenerate if already done, otherwise scrape fresh
      const forceRegenerate = result?.status === "done" && !!result?.caption;
      handleScraping(url, forceRegenerate);
    });
  };

  const handleUpdateLink = (index: number, val: string) => {
    const newLinks = [...links];
    newLinks[index] = val;
    setLinks(newLinks);
  };

  const handleAddLink = () => setLinks([...links, ""]);
  const handleRemoveLink = (index: number) => setLinks(links.filter((_, i) => i !== index));

  const onCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="min-h-screen bg-transparent text-white font-sans selection:bg-primary/30">
      <AppLayout view={view} setView={setView} setIsSettingsOpen={setIsSettingsOpen}>
        <AnimatePresence mode="wait">
          {view === "dashboard" && (
            <DashboardView
              activeBrand={activeBrand}
              links={links}
              results={results}
              count={generationCount}
              setCount={setGenerationCount}
              isABTest={isABTest}
              setIsABTest={setIsABTest}
              handleUpdateLink={handleUpdateLink}
              handleAddLink={handleAddLink}
              handleRemoveLink={handleRemoveLink}
              handleStartProcessing={handleStartProcessing}
              onRetry={handleScraping}
              onRegenerate={(url) => handleScraping(url, true)}
              onCopy={onCopy}
            />
          )}

          {view === "brand" && (
            <motion.div
              key="brand"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <BrandProfile onActiveBrandChange={fetchActiveBrand} />
            </motion.div>
          )}

          {view === "history" && (
            <motion.div
              key="history"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <HistoryView onCopy={onCopy} />
            </motion.div>
          )}
        </AnimatePresence>
      </AppLayout>

      <footer className="fixed bottom-0 right-0 p-4 z-0 pointer-events-none hidden md:block">
        <div className="text-[10px] font-black tracking-[0.2em] text-white/10 uppercase">
          POWERED BY TEKIBO
        </div>
      </footer>

      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        config={aiConfig}
        onSave={(newConfig) => {
          setAiConfig(newConfig);
          localStorage.setItem("ai_config", JSON.stringify(newConfig));
        }}
      />

      <BrandModal
        isOpen={isBrandModalOpen}
        onClose={() => setIsBrandModalOpen(false)}
        onSuccess={(brand) => {
          setActiveBrand(brand);
          handleStartProcessing();
        }}
      />
    </div>
  );
};

export default App;
